import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { scrollx_animate_black } from "../../../assets/icons";
import { flattenHelpArticles, helpCategoryPath, helpHomePath, helpSubheadingPath } from "../helpRoutes";

const PageContentDetails = ({
  selectedHeading,
  currPageContent,
  secondaryHeading,
  tertiarySubHeading,
  mainHeading,
  tertiaryId,
  loadPageContent,
  onGoHome,
  onOpenSupport,
  onFeedback,
}) => {
  const [tocItems, setTocItems] = useState([]);

  useEffect(() => {
    const contentContainer = document.getElementById("help-article-body");
    if (!contentContainer) {
      setTocItems([]);
      return;
    }
    const items = [...contentContainer.querySelectorAll("h2")].map((heading, index) => {
      const sectionId = heading.id || `section-${index}`;
      heading.id = sectionId;
      return { id: sectionId, title: heading.textContent };
    });
    setTocItems(items);

    const isGif = (img) =>
      /gif/i.test(img.getAttribute("src") || "") ||
      /gif/i.test(img.currentSrc || "") ||
      Boolean(img.closest(".gif-frame"));

    const lockGifHeight = (img) => {
      const attrHeight = img.getAttribute("height");
      const styleHeight = img.style.height;
      const heightPx = styleHeight || (attrHeight ? `${parseInt(attrHeight, 10)}px` : "");
      img.style.maxWidth = "100%";
      img.style.width = "100%";
      if (heightPx) {
        img.style.setProperty("height", heightPx, "important");
      }
    };

    contentContainer.querySelectorAll("img").forEach((img) => {
      if (!isGif(img)) return;
      if (img.complete) lockGifHeight(img);
      else img.addEventListener("load", () => lockGifHeight(img), { once: true });
    });
  }, [currPageContent, loadPageContent]);

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
      block: "start",
    });
  };

  if (loadPageContent) {
    return (
      <div className="help-loading flex items-center justify-center">
        <img src={scrollx_animate_black} className="w-[120px] h-[120px]" alt="" />
      </div>
    );
  }

  const relatedArticles = flattenHelpArticles(selectedHeading ? [selectedHeading] : [])
    .filter((article) => article.id !== tertiaryId)
    .slice(0, 4);

  return (
    <>
      <nav className="breadcrumbs" aria-label="Breadcrumb">
        <Link to={helpHomePath} onClick={onGoHome}>Help</Link>
        <span aria-hidden="true">/</span>
        <Link to={helpCategoryPath(mainHeading)}>{mainHeading}</Link>
        {secondaryHeading ? (
          <>
            <span aria-hidden="true">/</span>
            <Link to={helpSubheadingPath(mainHeading, secondaryHeading)}>{secondaryHeading}</Link>
          </>
        ) : null}
        <span aria-hidden="true">/</span>
        <span aria-current="page">{tertiarySubHeading}</span>
      </nav>

      <header className="article-header">
        <span className="eyebrow">{mainHeading}</span>
        <h1>{tertiarySubHeading}</h1>
        {secondaryHeading ? <p className="lede">{secondaryHeading}</p> : null}
      </header>

      <div className="article-grid">
        <article className="article">
          {currPageContent ? (
            <div
              id="help-article-body"
              dangerouslySetInnerHTML={{ __html: currPageContent }}
            />
          ) : (
            <div className="empty">No content available for this section.</div>
          )}

          {relatedArticles.length > 0 ? (
            <section className="related">
              <h2>Related guides</h2>
              <div className="related-grid">
                {relatedArticles.map((article) => (
                  <Link className="related-card" to={article.path} key={article.id}>
                    <strong>{article.title}</strong>
                    <span>{article.subHeading}</span>
                  </Link>
                ))}
              </div>
            </section>
          ) : null}

          <footer className="article-footer">
            <div className="feedback-row">
              <strong>Was this article helpful?</strong>
              <div className="feedback-actions">
                <button className="button" type="button" onClick={() => onFeedback?.("yes")}>Yes</button>
                <button className="button" type="button" onClick={() => onFeedback?.("no")}>No</button>
              </div>
            </div>
            <div className="support-row">
              <div>
                <strong>Still need help?</strong>
                <p>Contact support or review render troubleshooting.</p>
              </div>
              <button className="button primary" type="button" onClick={onOpenSupport}>
                Contact support
              </button>
            </div>
          </footer>
        </article>

        {tocItems.length > 0 ? (
          <aside className="toc" aria-label="On this page">
            <strong>On this page</strong>
            {tocItems.map((item) => (
              <button type="button" key={item.id} onClick={() => scrollToSection(item.id)}>
                {item.title}
              </button>
            ))}
          </aside>
        ) : null}
      </div>
    </>
  );
};

export default PageContentDetails;
